import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { CheckIcon, CloseIcon } from '../icons';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastItem {
  id: string;
  type: ToastType;
  message: ReactNode;
  duration?: number;
}

interface ToastContextValue {
  toast: (message: ReactNode, type?: ToastType, duration?: number) => void;
  success: (message: ReactNode, duration?: number) => void;
  error: (message: ReactNode, duration?: number) => void;
  warning: (message: ReactNode, duration?: number) => void;
  info: (message: ReactNode, duration?: number) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Fallback if rendered outside provider
    return {
      toast: (msg: ReactNode) => console.log('Toast:', msg),
      success: (msg: ReactNode) => console.log('Toast success:', msg),
      error: (msg: ReactNode) => console.error('Toast error:', msg),
      warning: (msg: ReactNode) => console.warn('Toast warning:', msg),
      info: (msg: ReactNode) => console.info('Toast info:', msg),
      dismiss: () => {},
    };
  }
  return ctx;
}

const TOAST_ICONS = {
  success: <CheckIcon size={16} className="text-success" />,
  error: <CloseIcon size={16} className="text-danger" />,
  warning: <span className="text-warning font-bold">!</span>,
  info: <span className="text-info font-bold">i</span>,
};

const TOAST_BORDERS = {
  success: 'border-s-4 border-s-success',
  error: 'border-s-4 border-s-danger',
  warning: 'border-s-4 border-s-warning',
  info: 'border-s-4 border-s-info',
};

const TOAST_SR_LABEL: Record<ToastType, string> = {
  success: 'Success',
  error: 'Error',
  warning: 'Warning',
  info: 'Info',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback((message: ReactNode, type: ToastType = 'info', duration = 4000) => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts((prev) => [...prev, { id, type, message, duration }]);

    if (duration > 0) {
      setTimeout(() => {
        dismiss(id);
      }, duration);
    }
  }, [dismiss]);

  // MUST be referentially stable. Pages put `toast` in useCallback/useEffect
  // dependency arrays; if this object is rebuilt on every render, then
  // "request fails → toast.error → provider re-renders → new `toast` →
  // loader effect re-fires → request fails → …" is an unbounded loop of
  // callable invocations. Observed on Broadcasting, Connect, Flows,
  // UserPermission, UserManager and BroadcastDetailDrawer.
  const value = useMemo<ToastContextValue>(() => ({
    toast: addToast,
    success: (msg, d) => addToast(msg, 'success', d),
    error: (msg, d) => addToast(msg, 'error', d),
    warning: (msg, d) => addToast(msg, 'warning', d),
    info: (msg, d) => addToast(msg, 'info', d),
    dismiss,
  }), [addToast, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Toast viewport container at bottom-end */}
      <div
        aria-live="polite"
        className="pointer-events-none fixed bottom-4 end-4 z-50 flex flex-col gap-2 max-w-sm w-full p-4"
      >
        {toasts.map((item) => (
          <div
            key={item.id}
            role="alert"
            className={cn(
              'pointer-events-auto flex items-center gap-3 rounded-card border border-line bg-white p-3.5 shadow-lg transition-all',
              'dark:border-slate-800 dark:bg-slate-900',
              TOAST_BORDERS[item.type],
            )}
          >
            <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-slate-50 dark:bg-slate-800">
              {TOAST_ICONS[item.type]}
              {/* Severity was colour-only; screen readers need the word. */}
              <span className="sr-only">{TOAST_SR_LABEL[item.type]}: </span>
            </div>
            <div className="min-w-0 flex-1 text-xs font-semibold text-ink dark:text-slate-200">
              {item.message}
            </div>
            <button
              type="button"
              onClick={() => dismiss(item.id)}
              className="shrink-0 text-ink-3 hover:text-ink dark:text-slate-400 dark:hover:text-slate-100"
              aria-label="Dismiss toast"
            >
              <CloseIcon size={14} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export default ToastProvider;
